import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  HeartPulse,
  ShieldCheck,
  Network,
  ClipboardList,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  Mail,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import brandWatermark from "@/assets/brand-watermark.png.asset.json";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const services = [
  {
    Icon: ShieldCheck,
    title: "NIS-2 und KRITIS Readiness für Kliniken",
    text: "Betroffenheitsprüfung (Krankenhäuser ab 100 Betten), Reifegradbestimmung und priorisierter Maßnahmenplan für Risikomanagement, Meldewege und Governance.",
  },
  {
    Icon: ClipboardList,
    title: "OT-Asset-Management im Klinikbetrieb",
    text: "Inventarisierung von HLK, Zutritt, USV, Medizingasen und Gebäudeleittechnik: passiv und aktiv erhoben, mit Kritikalitätsbewertung je Versorgungsprozess.",
  },
  {
    Icon: Network,
    title: "Netzarchitektur und sicherer Fernzugriff",
    text: "Zonen und Conduits nach IEC 62443 für GA, Medizintechnik-Umfeld und IT, inklusive Wartungszugängen für Hersteller und Dienstleister.",
  },
  {
    Icon: HeartPulse,
    title: "Betriebsmodell ohne Versorgungsrisiko",
    text: "Patch-, Change- und Incident-Prozesse, die im 24/7-Klinikbetrieb funktionieren: abgestimmt zwischen IT, Technischem Dienst, Medizintechnik und Geschäftsführung.",
  },
];

const scope = [
  "Fallen wir als Krankenhaus unter KRITIS und NIS-2, und ab wann?",
  "Welche gebäudetechnischen Systeme sind versorgungskritisch?",
  "Wie inventarisieren wir OT-Assets, ohne den Betrieb zu stören?",
  "Wie trennen wir Gebäudeautomation, Medizintechnik-Netze und IT sauber?",
  "Wie regeln wir Fernwartung durch Hersteller nachvollziehbar?",
  "Wer verantwortet OT-Security zwischen IT, Technik und Medizintechnik?",
];

const faqs = [
  {
    q: "Gilt NIS-2 für Krankenhäuser und ihre Gebäudeautomation?",
    a: "Ja. Krankenhäuser ab 100 Betten zählen in Deutschland zu KRITIS und fallen damit unter NIS-2. Die Gebäudeautomation (HLK, Zutritt, USV, Medizingase) gehört zu den abzusichernden OT-Systemen. Pflicht sind Risikoanalyse, Asset-Inventar, Patch- und Incident-Prozesse sowie technische Härtung nach IEC 62443.",
  },
  {
    q: "Was unterscheidet OT-Security im Krankenhaus von anderen Liegenschaften?",
    a: "Die Versorgungssicherheit steht über allem. Systeme lassen sich nicht beliebig abschalten, Wartungsfenster sind knapp und viele Anlagen hängen unmittelbar an Patientenprozessen. Deshalb arbeite ich zuerst mit passiven Erhebungsverfahren und plane aktive Scans oder Änderungen eng mit Technik und Medizintechnik abgestimmt.",
  },
  {
    q: "Wie grenzt sich OT-Security von Medizintechnik-Sicherheit ab?",
    a: "Medizinprodukte unterliegen eigenen regulatorischen Rahmen (MDR, Herstellerfreigaben). OT-Security adressiert die technische Gebäudeinfrastruktur und die Netze, in denen beides läuft. In der Praxis überschneiden sich die Zonen, daher definieren wir Verantwortlichkeiten und Übergabepunkte explizit.",
  },
  {
    q: "Womit sollte eine Klinik starten?",
    a: "Mit Sichtbarkeit: einem belastbaren Asset- und Topologie-Bild der gebäudetechnischen Systeme. Darauf folgen Kritikalitätsbewertung, Segmentierung und ein Betriebsmodell. Sichtbarkeit ist der Anfang, nicht das Ziel.",
  },
  {
    q: "Was kostet ein OT-Security-Audit für ein Klinikum?",
    a: "Je nach Umfang typischerweise zwischen 5.000 und 25.000 Euro. Preistreiber sind Anzahl der Standorte und Gewerke, die Tiefe der technischen Analyse sowie der Compliance-Rahmen (IEC 62443, NIS-2, KRITIS, B3S). Nach einem kostenlosen Erstgespräch erhalten Sie ein verbindliches Festpreisangebot.",
  },
];

const OtSecurityKrankenhaus = () => {
  const openContact = () =>
    window.dispatchEvent(new CustomEvent("open-contact-form"));

  const canonical = "https://fachsprechpartner.de/ot-security-krankenhaus";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: "OT-Security für Krankenhäuser und Kliniken",
        serviceType: "OT-Security, NIS-2 und KRITIS Beratung für Kliniken",
        provider: {
          "@type": "Organization",
          name: "FACHSPRECHPARTNER",
          url: "https://fachsprechpartner.de",
        },
        areaServed: { "@type": "Country", name: "Deutschland" },
        audience: {
          "@type": "BusinessAudience",
          audienceType:
            "Krankenhäuser, Kliniken, KRITIS-Betreiber, Technische Leitung, IT-Leitung",
        },
        description:
          "OT-Security für Krankenhäuser: NIS-2 und KRITIS Readiness, OT-Asset-Management der Gebäudeautomation, Netzsegmentierung nach IEC 62443 und ein Betriebsmodell für den 24/7-Klinikbetrieb.",
        url: canonical,
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fachsprechpartner.de/" },
          { "@type": "ListItem", position: 2, name: "OT-Security Krankenhaus", item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>OT-Security Krankenhaus: NIS-2 & KRITIS für Kliniken</title>
        <meta
          name="description"
          content="OT-Security für Krankenhäuser: NIS-2 und KRITIS Readiness, Asset-Management der Gebäudeautomation und Segmentierung nach IEC 62443. Erstgespräch anfragen."
        />
        <meta name="keywords" content="OT-Security Krankenhaus, Krankenhaus NIS2, KRITIS Krankenhaus, Klinik Cybersicherheit, Gebäudeautomation Krankenhaus, IEC 62443 Klinik" />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="de" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={canonical} />
        <meta property="og:title" content="OT-Security Krankenhaus: NIS-2 & KRITIS für Kliniken" />
        <meta
          property="og:description"
          content="OT-Security für Krankenhäuser: NIS-2 und KRITIS Readiness, Asset-Management der Gebäudeautomation und Segmentierung nach IEC 62443."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FACHSPRECHPARTNER Tim Bauer, OT-Security und Gebäudeautomation" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content="Fachsprechpartner" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OT-Security Krankenhaus: NIS-2 & KRITIS für Kliniken" />
        <meta name="twitter:description" content="OT-Security für Krankenhäuser: NIS-2 und KRITIS Readiness, Asset-Management der Gebäudeautomation und Segmentierung nach IEC 62443." />
        <meta name="twitter:image" content="https://fachsprechpartner.de/og-image.webp" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-primary to-primary/90 text-primary-foreground">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent text-xs font-semibold uppercase tracking-wider mb-6">
                <HeartPulse className="w-4 h-4" />
                Krankenhaus und Klinik
              </div>
              <h1 className="text-4xl md:text-6xl font-heading font-bold leading-tight mb-6">
                OT-Security für Krankenhäuser: NIS-2 und KRITIS im laufenden Betrieb umsetzen.
              </h1>
              <p className="text-lg md:text-xl text-primary-foreground/85 mb-8 leading-relaxed max-w-3xl">
                Ich sichere die technische Infrastruktur von Kliniken ab: Gebäudeautomation,
                HLK, Zutritt, USV und Medizingase. Nach IEC 62443, entlang der NIS-2 Pflichten
                und immer mit Blick auf die Versorgungssicherheit.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={openContact}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
                >
                  Erstgespräch anfragen <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/workbook"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 text-primary-foreground font-semibold hover:bg-primary-foreground/10 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  Zum Workbook
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Leistungen */}
        <section className="relative isolate overflow-hidden py-16 md:py-24">
          <img
            src={brandWatermark.url}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="clinic-brand-watermark pointer-events-none absolute -bottom-12 -right-36 w-72 max-w-none select-none md:-bottom-24 md:-right-48 md:w-[30rem]"
          />
          <div className="relative z-10 max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Leistungen für Kliniken
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl">
              Vier Bausteine, die aufeinander aufbauen und sich in den Klinikalltag
              einfügen, ohne Versorgungsprozesse zu gefährden.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map(({ Icon, title, text }) => (
                <div
                  key={title}
                  className="p-6 rounded-2xl border border-border bg-card hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-2">{title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Fragen */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Diese Fragen klären wir gemeinsam
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Statt einer generischen Compliance-Checkliste starten wir mit den Fragen,
                die im Klinikbetrieb wirklich zählen, zwischen Technischem Dienst,
                Medizintechnik und IT.
              </p>
            </div>
            <ul className="space-y-3">
              {scope.map((s) => (
                <li key={s} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                  <span className="text-foreground/90">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Weiterführend */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">
              Passende Vertiefungen
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { to: "/nis2", label: "NIS-2 Richtlinie" },
                { to: "/iec62443", label: "IEC 62443" },
                { to: "/ot-asset-management", label: "OT-Asset-Management" },
                { to: "/ot-netzwerkarchitektur", label: "OT-Netzwerkarchitektur" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="p-5 rounded-xl border border-border bg-card hover:border-accent transition-colors flex items-center justify-between gap-2"
                >
                  <span className="font-semibold">{l.label}</span>
                  <ArrowRight className="w-4 h-4 text-accent flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">
              Häufige Fragen zu OT-Security im Krankenhaus
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left font-semibold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Bereit für den nächsten Schritt?
            </h2>
            <p className="text-primary-foreground/85 mb-8 leading-relaxed">
              Starten Sie mit dem Workbook oder buchen Sie direkt ein Assessment für Ihr
              Haus. In beiden Fällen bekommen Sie einen priorisierten Fahrplan.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/workbook"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Workbook ansehen
              </Link>
              <button
                onClick={openContact}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Sparring anfragen
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default OtSecurityKrankenhaus;